import { track } from '@lib/sdk/api/modules/track'
import { CDP_EVENT_TYPE, CLIENT_TYPE } from '@lib/constants/cdp.const'

interface CommentTrackingPayload {
  canvasId?: string
  componentId?: string
  threadId: string
  commentId?: string
  action: string
  content?: string
  authorId?: string | number
  authorName?: string
  authorEmail?: string
  assigneeId?: string | number
  assigneeName?: string
  assigneeEmail?: string
  resolved?: boolean
  deleted?: boolean
  timestamp?: string
}

export function useCommentTracking() {
  const detectClient = (): string => {
    const url = window.location.href
    if (url.includes('/admin/')) {
      return CLIENT_TYPE.admin
    } else {
      return CLIENT_TYPE.impact
    }
  }

  const trackCommentEvent = async (eventType: string, payload: CommentTrackingPayload) => {
    try {
      const client = detectClient()
      await track({
        event_name: eventType,
        payload: {
          ...payload,
          client,
          timestamp: payload.timestamp || new Date().toISOString(),
        },
      })
    } catch (error) {
      console.warn(`Failed to track comment event ${eventType}:`, error)
    }
  }

  const trackCommentAdded = (payload: Omit<CommentTrackingPayload, 'action'>) => {
    return trackCommentEvent(CDP_EVENT_TYPE.commentAdded, {
      ...payload,
      action: 'comment_added',
    })
  }

  const trackCommentReplied = (payload: Omit<CommentTrackingPayload, 'action'>) => {
    return trackCommentEvent(CDP_EVENT_TYPE.commentReplied, {
      ...payload,
      action: 'comment_replied',
    })
  }

  const trackCommentAssigned = (payload: Omit<CommentTrackingPayload, 'action'>) => {
    return trackCommentEvent(CDP_EVENT_TYPE.commentAssigned, {
      ...payload,
      action: 'comment_assigned',
    })
  }

  const trackCommentResolved = (payload: Omit<CommentTrackingPayload, 'action'>) => {
    return trackCommentEvent(CDP_EVENT_TYPE.commentResolved, {
      ...payload,
      action: 'comment_resolved',
      resolved: true,
    })
  }

  const trackCommentDeleted = (payload: Omit<CommentTrackingPayload, 'action'>) => {
    return trackCommentEvent(CDP_EVENT_TYPE.commentDeleted, {
      ...payload,
      action: 'comment_deleted',
      deleted: true,
    })
  }

  return {
    trackCommentAdded,
    trackCommentReplied,
    trackCommentAssigned,
    trackCommentResolved,
    trackCommentDeleted,
  }
}
